/**
 * InputsPill — "What Built This Price" interaction
 *
 * Collapsed: overlapping mini circles + "9 inputs" pill (border-radius: 100).
 * Expanded: verification animation (first open only), then data panel with
 * source-attributed rows organized by Cost Floor and Price Verdict.
 */

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import type { Verdict } from '@/types';
import type { UserProfile } from '@/types';
import { MEDIUMS } from '@/types';
import { SourceCircle, SOURCE_CONFIG } from './SourceCircle';
import type { InputSource, CircleState } from './SourceCircle';
import { InputsDataRow } from './InputsDataRow';

const TEAL = '#3DABA5';
const CHARCOAL = '#1A1A1A';
const FONT = "'Manrope', 'Inter', -apple-system, sans-serif";

// ── Keyframes (injected once) ──────────────────────────────────────

type MorphPhase = 'pill' | 'pill-exit' | 'panel-enter' | 'panel' | 'panel-exit' | 'pill-enter';

const SPRING = 'cubic-bezier(0.34, 1.56, 0.64, 1)';
const EASE_OUT = 'cubic-bezier(0.16, 1, 0.3, 1)';

// Durations (ms)
const PILL_EXIT_MS = 180;
const PANEL_ENTER_MS = 380;
const PANEL_EXIT_MS = 200;
const PILL_ENTER_MS = 320;

function injectKeyframes() {
  if (document.getElementById('inputs-pill-kf')) return;
  const style = document.createElement('style');
  style.id = 'inputs-pill-kf';
  style.textContent = `
    @keyframes srcRing {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    @keyframes srcCheck {
      0% { stroke-dashoffset: 14; }
      100% { stroke-dashoffset: 0; }
    }
    @keyframes fadeUp {
      0% { opacity: 0; transform: translateY(8px); }
      100% { opacity: 1; transform: translateY(0); }
    }
    @keyframes shimmer {
      0% { background-position: -200px 0; }
      100% { background-position: 200px 0; }
    }
    @keyframes popIn {
      0% { transform: scale(0); }
      60% { transform: scale(1.2); }
      100% { transform: scale(1); }
    }
    @keyframes pillExit {
      0%   { opacity: 1; transform: scale(1); }
      100% { opacity: 0; transform: scale(0.88); }
    }
    @keyframes panelEnter {
      0%   { opacity: 0; transform: translateY(16px) scale(0.97); }
      100% { opacity: 1; transform: translateY(0) scale(1); }
    }
    @keyframes panelExit {
      0%   { opacity: 1; transform: scale(1); }
      100% { opacity: 0; transform: translateY(10px) scale(0.97); }
    }
    @keyframes pillEnter {
      0%   { opacity: 0; transform: scale(0.8); }
      70%  { transform: scale(1.06); }
      100% { opacity: 1; transform: scale(1); }
    }
  `;
  document.head.appendChild(style);
}

// ── Helpers ──────────────────────────────────────────────────────────

function currency(cents: number) {
  return '$' + Math.round(cents / 100).toLocaleString();
}

// ── Data row definitions ─────────────────────────────────────────────

interface DataItem {
  label: string;
  value: string;
  source: InputSource;
  note?: string | null;
  section: 'floor' | 'verdict';
}

function buildDataRows(v: Verdict, profile: UserProfile | null): DataItem[] {
  const burnSource: InputSource = profile?.bankConnected ? 'bank' : 'setup';
  const burnNote = profile?.bankConnected ? '3-mo avg' : null;
  const hoursNote = v.inputs.hoursTracked ? null : 'Estimated';

  const weeklyCapacity = Math.round(v.inputs.monthlyCapacity / 4.33);
  const minHourly = v.calculations.survivalTarget;
  const askingPrice = v.inputs.gutPrice ?? v.calculations.ttcp;
  const effHourly = v.calculations.effectiveHourly != null
    ? Math.round(v.calculations.effectiveHourly / 100) : 0;
  const sustainability = v.calculations.sustainabilityRatio ?? 0;
  const mediumLabel = MEDIUMS[v.inputs.medium]?.label || v.inputs.medium;

  const rows: DataItem[] = [
    // Cost Floor
    { label: 'Monthly costs', value: currency(v.inputs.monthlyBurn), source: burnSource, note: burnNote, section: 'floor' },
    { label: 'Weekly capacity', value: `${weeklyCapacity} hrs`, source: 'setup', section: 'floor' },
    { label: 'Hours on this piece', value: `${v.inputs.hours} hrs`, source: 'manual', note: hoursNote, section: 'floor' },
    { label: 'Materials', value: currency(v.inputs.materials), source: 'manual', section: 'floor' },
    { label: 'Minimum hourly', value: `${currency(minHourly)}/hr`, source: 'derived', note: 'burn ÷ capacity', section: 'floor' },

    // Price Verdict
    { label: 'Your asking price', value: currency(askingPrice), source: 'manual', section: 'verdict' },
    { label: 'Medium', value: mediumLabel, source: 'manual', section: 'verdict' },
    { label: 'Dimensions', value: `${v.inputs.width}" × ${v.inputs.height}"`, source: 'manual', section: 'verdict' },
    { label: 'Career stage', value: v.inputs.careerStage, source: 'setup', section: 'verdict' },
    { label: 'Effective Hourly Rate', value: `$${effHourly}/hr`, source: 'derived', note: 'price ÷ hrs', section: 'verdict' },
  ];

  return rows;
}

function getSourceCounts(rows: DataItem[]): Record<InputSource, number> {
  const counts: Record<InputSource, number> = { bank: 0, setup: 0, manual: 0, derived: 0 };
  for (const row of rows) counts[row.source]++;
  return counts;
}

// ── Shimmer rows ────────────────────────────────────────────────────

function ShimmerRows() {
  return (
    <div style={{ padding: '8px 0' }}>
      {[0.92, 1, 0.78].map((w, i) => (
        <div key={i} style={{
          height: 14, borderRadius: 4, marginTop: i > 0 ? 12 : 0,
          width: `${w * 100}%`,
          background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
          backgroundSize: '400px 14px',
          animation: 'shimmer 1.5s ease-in-out infinite',
        }} />
      ))}
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────────────

interface InputsPillProps {
  verdict: Verdict;
  profile?: UserProfile | null;
}

export function InputsPill({ verdict, profile }: InputsPillProps) {
  const [morph, setMorph] = useState<MorphPhase>('pill');
  const hasPlayed = useRef(false);
  const [pressed, setPressed] = useState(false);

  // Verification animation state
  const [phase, setPhase] = useState<'idle' | 'verifying' | 'done'>('idle');
  const [circleStates, setCircleStates] = useState<Record<InputSource, CircleState>>({
    bank: 'idle', setup: 'idle', manual: 'idle', derived: 'idle',
  });
  const [resolvedCount, setResolvedCount] = useState(0);
  const [activeFilter, setActiveFilter] = useState<InputSource | null>(null);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const morphTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Inject keyframes on mount
  useEffect(() => { injectKeyframes(); }, []);

  // Data
  const dataRows = useMemo(() => buildDataRows(verdict, profile ?? null), [verdict, profile]);
  const sourceCounts = useMemo(() => getSourceCounts(dataRows), [dataRows]);
  // 9 core inputs: burn, capacity, hours, materials, medium, dimensions, career, tier, location
  // Derived values (min hourly, eff hourly) are displayed but not counted
  const inputCount = 9 + (verdict.inputs.gutPrice ? 1 : 0);

  // Which sources are actually used (have count > 0)
  const activeSources = useMemo(() => {
    const order: InputSource[] = ['bank', 'setup', 'manual', 'derived'];
    return order.filter(s => sourceCounts[s] > 0);
  }, [sourceCounts]);

  // Mini circle colors for collapsed pill
  const miniColors = useMemo(() => activeSources.map(s => SOURCE_CONFIG[s].color), [activeSources]);

  // Filtered data rows
  const visibleRows = useMemo(() => {
    if (!activeFilter) return dataRows;
    return dataRows.filter(r => r.source === activeFilter);
  }, [dataRows, activeFilter]);

  const floorRows = useMemo(() => visibleRows.filter(r => r.section === 'floor'), [visibleRows]);
  const verdictRows = useMemo(() => visibleRows.filter(r => r.section === 'verdict'), [visibleRows]);

  // Cleanup timers
  useEffect(() => {
    return () => { timersRef.current.forEach(t => clearTimeout(t)); };
  }, []);

  // ── Verification sequence ──────────────────────────────────────────

  const runVerification = useCallback(() => {
    setPhase('verifying');
    setResolvedCount(0);
    setCircleStates({ bank: 'idle', setup: 'idle', manual: 'idle', derived: 'idle' });

    const sources = activeSources;
    let resolved = 0;

    sources.forEach((src, i) => {
      // Start loading
      const loadTimer = setTimeout(() => {
        setCircleStates(prev => ({ ...prev, [src]: 'loading' }));
      }, i * 320);
      timersRef.current.push(loadTimer);

      // Resolve
      const resolveTimer = setTimeout(() => {
        setCircleStates(prev => ({ ...prev, [src]: 'resolved' }));
        resolved++;
        setResolvedCount(resolved);

        if (resolved === sources.length) {
          setTimeout(() => {
            setPhase('done');
            hasPlayed.current = true;
          }, 200);
        }
      }, i * 320 + 280);
      timersRef.current.push(resolveTimer);
    });
  }, [activeSources]);

  // ── Scroll into view when panel enters ────────────────────────────
  useEffect(() => {
    if (morph === 'panel-enter' && panelRef.current) {
      // Small delay so the DOM has rendered the panel
      requestAnimationFrame(() => {
        panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [morph]);

  // ── Morph cleanup ────────────────────────────────────────────────
  useEffect(() => {
    return () => { if (morphTimerRef.current) clearTimeout(morphTimerRef.current); };
  }, []);

  // ── Open / Close ──────────────────────────────────────────────────

  const handleOpen = useCallback(() => {
    if (morph !== 'pill') return;
    setActiveFilter(null);

    // Start pill exit animation
    setMorph('pill-exit');
    morphTimerRef.current = setTimeout(() => {
      // Pill exit done → panel enters
      setMorph('panel-enter');

      if (hasPlayed.current) {
        const resolved: Record<InputSource, CircleState> = { bank: 'idle', setup: 'idle', manual: 'idle', derived: 'idle' };
        for (const s of activeSources) resolved[s] = 'resolved';
        setCircleStates(resolved);
        setResolvedCount(activeSources.length);
        setPhase('done');
      } else {
        runVerification();
      }

      morphTimerRef.current = setTimeout(() => {
        setMorph('panel');
      }, PANEL_ENTER_MS);
    }, PILL_EXIT_MS);
  }, [morph, activeSources, runVerification]);

  const handleClose = useCallback(() => {
    if (morph !== 'panel' && morph !== 'panel-enter') return;

    setMorph('panel-exit');
    morphTimerRef.current = setTimeout(() => {
      // Reset verification state
      setPhase('idle');
      setActiveFilter(null);
      setResolvedCount(0);
      setCircleStates({ bank: 'idle', setup: 'idle', manual: 'idle', derived: 'idle' });
      timersRef.current.forEach(t => clearTimeout(t));
      timersRef.current = [];

      // Pill re-enters with spring
      setMorph('pill-enter');
      morphTimerRef.current = setTimeout(() => {
        setMorph('pill');
      }, PILL_ENTER_MS);
    }, PANEL_EXIT_MS);
  }, [morph]);

  // ── Filter toggle ─────────────────────────────────────────────────

  const handleSourceTap = useCallback((src: InputSource) => {
    if (phase !== 'done') return;
    setActiveFilter(prev => prev === src ? null : src);
  }, [phase]);

  // ── Render ─────────────────────────────────────────────────────────

  const sustainability = verdict.calculations.sustainabilityRatio ?? 0;
  const showStagger = !hasPlayed.current && phase === 'done';

  const showPill = morph === 'pill' || morph === 'pill-exit' || morph === 'pill-enter';
  const showPanel = morph === 'panel-enter' || morph === 'panel' || morph === 'panel-exit';

  // Pill animation style
  const pillAnimation =
    morph === 'pill-exit' ? `pillExit ${PILL_EXIT_MS}ms ${EASE_OUT} forwards` :
    morph === 'pill-enter' ? `pillEnter ${PILL_ENTER_MS}ms ${SPRING} both` :
    undefined;

  // Panel animation style
  const panelAnimation =
    morph === 'panel-enter' ? `panelEnter ${PANEL_ENTER_MS}ms ${SPRING} both` :
    morph === 'panel-exit' ? `panelExit ${PANEL_EXIT_MS}ms ${EASE_OUT} forwards` :
    undefined;

  return (
    <div style={{ position: 'relative' }}>
      {/* ── Collapsed: Pill ────────────────────────────────────────── */}
      {showPill && (
        <div style={{
          display: 'flex', justifyContent: 'center',
          animation: pillAnimation,
        }}>
          <button
            onClick={handleOpen}
            onPointerDown={() => setPressed(true)}
            onPointerUp={() => setPressed(false)}
            onPointerLeave={() => setPressed(false)}
            onPointerCancel={() => setPressed(false)}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 16px 8px 10px',
              borderRadius: 100,
              backgroundColor: 'white',
              border: '1px solid #E7E5E4',
              boxShadow: pressed
                ? '0 0px 1px rgba(0,0,0,0.04)'
                : '0 1px 3px rgba(0,0,0,0.04)',
              cursor: 'pointer',
              fontFamily: FONT,
              transition: `box-shadow 0.2s ease, border-color 0.2s ease, transform 0.18s ${SPRING}`,
              transform: pressed ? 'scale(0.96)' : 'scale(1)',
              WebkitTapHighlightColor: 'transparent',
            }}
            onMouseEnter={(e) => {
              if (!pressed) {
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                e.currentTarget.style.borderColor = '#D6D3D1';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)';
              e.currentTarget.style.borderColor = '#E7E5E4';
            }}
          >
            {/* Overlapping mini circles */}
            <div style={{ display: 'flex' }}>
              {miniColors.map((color, i) => (
                <div key={i} style={{
                  width: 20, height: 20, borderRadius: '50%',
                  backgroundColor: color,
                  border: '2px solid white',
                  marginLeft: i > 0 ? -6 : 0,
                  position: 'relative',
                  zIndex: miniColors.length - i,
                }} />
              ))}
            </div>

            {/* Count */}
            <span style={{
              fontSize: 13, fontWeight: 600, color: CHARCOAL,
            }}>{inputCount} inputs</span>
          </button>
        </div>
      )}

      {/* ── Expanded: Panel ──────────────────────────────────────── */}
      {showPanel && (
        <div ref={panelRef} style={{
          borderRadius: 16, border: '1px solid #E7E5E4',
          backgroundColor: 'white',
          boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
          animation: panelAnimation,
          fontFamily: FONT,
          overflow: 'hidden',
        }}>
      {/* Header — entire row is tappable to minimize */}
      <div
        onClick={handleClose}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 14px 0',
          cursor: 'pointer',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Mini circles in header */}
          <div style={{ display: 'flex' }}>
            {miniColors.map((color, i) => (
              <div key={i} style={{
                width: 14, height: 14, borderRadius: '50%',
                backgroundColor: color,
                border: '1.5px solid white',
                marginLeft: i > 0 ? -4 : 0,
                position: 'relative',
                zIndex: miniColors.length - i,
              }} />
            ))}
          </div>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#78716C' }}>
            {phase === 'verifying'
              ? `Checking ${resolvedCount} of ${activeSources.length}...`
              : `${inputCount} inputs`
            }
          </span>
        </div>

        {/* Close icon (visual affordance, click handled by parent) */}
        <div
          style={{
            width: 28, height: 28, borderRadius: '50%',
            backgroundColor: '#F5F5F4',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2.5 2.5L9.5 9.5M9.5 2.5L2.5 9.5" stroke="#A8A29E" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </div>
      </div>

      {/* Source circles */}
      <div style={{
        display: 'flex', justifyContent: 'center', gap: 20,
        padding: '16px 16px 12px',
      }}>
        {activeSources.map(src => (
          <SourceCircle
            key={src}
            source={src}
            state={circleStates[src]}
            count={sourceCounts[src]}
            activeFilter={activeFilter}
            onTap={() => handleSourceTap(src)}
          />
        ))}
      </div>

      {/* Filter pill */}
      {activeFilter && phase === 'done' && (
        <div style={{
          display: 'flex', justifyContent: 'center', paddingBottom: 8,
        }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '4px 10px 4px 8px',
            borderRadius: 100, backgroundColor: `${SOURCE_CONFIG[activeFilter].color}08`,
            border: `1px solid ${SOURCE_CONFIG[activeFilter].color}20`,
          }}>
            <div style={{
              width: 6, height: 6, borderRadius: '50%',
              backgroundColor: SOURCE_CONFIG[activeFilter].color,
            }} />
            <span style={{
              fontSize: 11, fontWeight: 600, color: SOURCE_CONFIG[activeFilter].color,
            }}>{SOURCE_CONFIG[activeFilter].label} inputs</span>
            <button
              onClick={() => setActiveFilter(null)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 10, fontWeight: 600, color: '#A8A29E',
                padding: '0 0 0 2px',
              }}
            >Clear</button>
          </div>
        </div>
      )}

      {/* Data rows */}
      <div style={{ padding: '0 16px 18px' }}>
        {phase !== 'done' ? (
          <ShimmerRows />
        ) : (
          <>
            {/* Cost Floor section */}
            {floorRows.length > 0 && (
              <>
                <div style={{
                  fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: '#C4B5A4',
                  textTransform: 'uppercase' as const, padding: '10px 0 4px',
                  animation: showStagger ? 'fadeUp 0.25s ease both' : undefined,
                  animationDelay: showStagger ? '0ms' : undefined,
                }}>Cost Floor</div>
                {floorRows.map((row, i) => (
                  <div key={row.label} style={{
                    animation: showStagger ? `fadeUp 0.25s ease both` : undefined,
                    animationDelay: showStagger ? `${(i + 1) * 40}ms` : undefined,
                  }}>
                    <InputsDataRow
                      label={row.label}
                      value={row.value}
                      source={row.source}
                      note={row.note}
                      divider={i < floorRows.length - 1}
                    />
                  </div>
                ))}
                {/* Floor total */}
                {!activeFilter && (
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '14px 0 16px', marginTop: 2,
                    borderTop: `1px solid ${TEAL}15`,
                    animation: showStagger ? `fadeUp 0.25s ease both` : undefined,
                    animationDelay: showStagger ? `${(floorRows.length + 1) * 40}ms` : undefined,
                  }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: TEAL }}>Floor (TTCP)</span>
                    <span style={{
                      fontSize: 18, fontWeight: 800, color: TEAL,
                      fontVariantNumeric: 'tabular-nums',
                    }}>{currency(verdict.calculations.ttcp)}</span>
                  </div>
                )}
              </>
            )}

            {/* Price Verdict section */}
            {verdictRows.length > 0 && (
              <>
                <div style={{
                  fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: '#C4B5A4',
                  textTransform: 'uppercase' as const, padding: '10px 0 4px',
                  animation: showStagger ? 'fadeUp 0.25s ease both' : undefined,
                  animationDelay: showStagger
                    ? `${(floorRows.length + 2) * 40}ms`
                    : undefined,
                }}>Price Verdict</div>
                {verdictRows.map((row, i) => (
                  <div key={row.label} style={{
                    animation: showStagger ? `fadeUp 0.25s ease both` : undefined,
                    animationDelay: showStagger
                      ? `${(floorRows.length + 3 + i) * 40}ms`
                      : undefined,
                  }}>
                    <InputsDataRow
                      label={row.label}
                      value={row.value}
                      source={row.source}
                      note={row.note}
                      divider={i < verdictRows.length - 1}
                    />
                  </div>
                ))}
                {/* Sustainability total */}
                {!activeFilter && (
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '14px 0 0', marginTop: 2,
                    borderTop: `1px solid ${TEAL}15`,
                    animation: showStagger ? `fadeUp 0.25s ease both` : undefined,
                    animationDelay: showStagger
                      ? `${(floorRows.length + verdictRows.length + 3) * 40}ms`
                      : undefined,
                  }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: TEAL }}>Sustainability</span>
                    <span style={{
                      fontSize: 18, fontWeight: 800, color: TEAL,
                      fontVariantNumeric: 'tabular-nums',
                    }}>{(Math.round(sustainability * 10) / 10)}x</span>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
      )}
    </div>
  );
}
